import type { LetterHead, Meeting } from "@repo/types";

export interface GenerateExampleMeeting {
  letterHead?: LetterHead;
}

export const generateMeetingExample = ({
  letterHead,
}: GenerateExampleMeeting) => {
  const date = new Date("2020-01-05T10:00:00");
  const endTime = new Date("2020-01-05T:11:00:00");

  const core = {
    _deleted: false,
    _schemaVersion: 1,
    _source: "server",
    _version: 1,
    createdAt: date,
    createdBy: "user",
    updatedAt: date,
    updatedBy: "user",
    teamId: "team",
  } as const;

  const meetingMeta = {
    ...core,
    description: "",
    status: "draft",
    publishedAt: date,
    startTime: date,
    endTime,
    letterHead: null,
  } as const;

  const agendaMeta = {
    ...core,
    description: "",
    status: "draft",
  } as const;

  const sectionMeta = {
    ...core,
    status: "draft",
    body: "",
    type: "",
    description: "",
  } as const;

  const itemMeta = {
    ...core,
    body: "",
    type: "",
    description: "",
    status: "draft" as const,
    submittedBy: "user",
    documentIds: [],
    documents: [],
  };

  const docMeta = {
    ...core,
    status: "draft" as const,
    description: "",
    attachments: [],
  };

  //////////////////////////////

  const meetingExample: Meeting = {
    ...meetingMeta,
    id: "meeting",
    name: "Example Meeting",
    agendaId: "agenda",
    agenda: {
      ...agendaMeta,
      id: "agenda",
      name: "Agenda",
      letterHeadId: letterHead?.id ?? null,
      letterHead: letterHead ?? null,
      agendaModuleIds: [
        "section1",
        "section2",
        "section3",
        "section4",
        "section5",
        "section6",
      ],
      agendaModules: [
        {
          ...sectionMeta,
          id: "section1",
          name: "Opening",
          agendaItemIds: ["item1", "item2", "item3", "item4"],
          agendaItems: [
            {
              ...itemMeta,
              id: "item1",
              name: "Call to Order",
            },
            {
              ...itemMeta,
              id: "item1",
              name: "Pledge of Allegiance",
            },
            {
              ...itemMeta,
              id: "item3",
              name: "Announcements",
            },
            {
              ...itemMeta,
              id: "item3",
              name: "Public Comments on Agenda Items",
            },
          ],
        },
        {
          ...sectionMeta,
          id: "section2",
          teamId: "team",
          name: "Reports",
          status: "draft",
          agendaItemIds: ["item5", "item6"],
          agendaItems: [
            {
              ...itemMeta,
              id: "item5",
              name: "Minutes",
              documentIds: ["doc1"],
              documents: [
                {
                  ...docMeta,
                  id: "doc1",
                  name: "Minutes from last meeting",
                  linkedAttachment: "attachment",
                  attachments: [
                    {
                      attachmentId: "attachment",
                      attachmentType: "original",
                      name: "Attachment",
                      path: "",
                      url: "#",
                      isUrl: true,
                    },
                  ],
                },
              ],
            },
            {
              ...itemMeta,
              id: "item6",
              name: "Reports",
            },
          ],
        },
        {
          ...sectionMeta,
          id: "section3",
          teamId: "team",
          name: "Unfinished Business",
          status: "draft",
          agendaItemIds: ["item7"],
          agendaItems: [
            {
              ...itemMeta,
              id: "item7",
              name: "Employee Handbook",
              documentIds: ["doc2"],
              documents: [
                {
                  ...docMeta,
                  id: "doc2",
                  name: "Draft Document (no attachment)",
                },
              ],
            },
          ],
        },
        {
          ...sectionMeta,
          id: "section4",
          teamId: "team",
          name: "New Business",
          status: "draft",
          agendaItemIds: ["item8"],
          agendaItems: [
            {
              ...itemMeta,
              id: "item8",
              name: "Engineering plan for Example Road",
              documentIds: ["doc3"],
              documents: [
                {
                  ...docMeta,
                  id: "doc3",
                  name: "Unofficial plan details (confidential)",
                  confidential: true,
                },
              ],
            },
          ],
        },
        {
          ...sectionMeta,
          id: "section5",
          teamId: "team",
          name: "Public Comments",
          status: "draft",
          agendaItemIds: ["item9"],
          agendaItems: [
            {
              ...itemMeta,
              id: "item9",
              name: "General Comments from the Public",
            },
          ],
        },
        {
          ...sectionMeta,
          id: "section6",
          teamId: "team",
          name: "Closing",
          status: "draft",
          agendaItemIds: ["item10"],
          agendaItems: [
            {
              ...itemMeta,
              id: "item10",
              name: "Supervisor Comments",
            },
          ],
        },
      ],
    },
  };

  return meetingExample;
};
